import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import type { EventType } from '@/lib/eventCatalog';

/**
 * Fire-and-forget activity tracker. Mounted once inside ProtectedRoute.
 *
 * - Logs `login` at most ONCE PER BROWSER SESSION PER USER (sessionStorage-keyed,
 *   NOT ref-keyed). A component ref only survives as long as the component: a
 *   remount loop in the route guard previously re-fired `login` on every mount,
 *   writing 6,085 rows in 20 minutes (~5/second) for a single user and poisoning
 *   87% of platform telemetry. Session-scoped state is immune to remounts.
 * - Logs `page_view` on every route change (debounced 300ms)
 * - Exposes `trackAction()` for explicit business events
 *
 * Every insert also passes through a module-level throttle that drops an
 * identical (event_type + metadata) write inside THROTTLE_MS. This is the
 * client half of the guarantee; a partial unique index on user_activity_log
 * enforces the `login` rule server-side so a broken client cannot flood it.
 *
 * ALL inserts are wrapped in try/catch. Failures are silently logged
 * to the console. This hook NEVER throws, NEVER blocks rendering,
 * and NEVER updates any state that could trigger re-renders.
 */

/** Minimum gap between two byte-identical events from one tab. */
const THROTTLE_MS = 2_000;

/** event signature -> last send time (module scope: survives remounts). */
const lastSentAt = new Map<string, number>();

function loginSessionKey(userId: string) {
  return `activity_login_logged:${userId}`;
}

/** True when this browser session has already recorded a login for this user. */
function loginAlreadyRecorded(userId: string): boolean {
  try {
    return sessionStorage.getItem(loginSessionKey(userId)) === '1';
  } catch {
    // Private mode / storage disabled: fall back to the module throttle only.
    return false;
  }
}

function markLoginRecorded(userId: string) {
  try {
    sessionStorage.setItem(loginSessionKey(userId), '1');
  } catch {
    // ignore — throttle still applies
  }
}

export function useActivityTracker(userId: string | undefined, companyId: string | undefined) {
  const location = useLocation();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- silent insert helper ---
  const logEvent = useCallback(
    (eventType: EventType, metadata?: Record<string, unknown>) => {
      try {
        if (!userId) return;

        // Drop identical repeats inside the throttle window. Keyed on user +
        // event + metadata so distinct pages/listings still record normally.
        const signature = `${userId}|${eventType}|${JSON.stringify(metadata ?? {})}`;
        const now = Date.now();
        const previous = lastSentAt.get(signature);
        if (previous !== undefined && now - previous < THROTTLE_MS) return;
        lastSentAt.set(signature, now);

        const promise = supabase
          .from('user_activity_log' as any)
          .insert({
            user_id: userId,
            company_id: companyId ?? null,
            event_type: eventType,
            event_metadata: metadata ?? {},
          })
          .then(({ error }) => {
            if (error) {
              console.debug('[ActivityTracker] insert error (non-fatal):', error.message);
            }
          });
        // Handle network-level failures silently
        Promise.resolve(promise).catch(() => {});
      } catch {
        // Absolute safety net — never throw from this hook
      }
    },
    [userId, companyId],
  );

  // --- login event: once per browser session per user (remount-proof) ---
  useEffect(() => {
    try {
      if (!userId) return;
      if (loginAlreadyRecorded(userId)) return;
      // Mark BEFORE inserting: if this component remounts while the insert is
      // still in flight, the second mount must not queue another row.
      markLoginRecorded(userId);
      logEvent('login');
    } catch {
      // never throw
    }
  }, [userId, logEvent]);

  // --- page_view on route change (debounced) ---
  useEffect(() => {
    try {
      if (!userId) return;

      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        logEvent('page_view', { page: location.pathname });
      }, 300);

      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    } catch {
      // never throw
    }
  }, [userId, location.pathname, logEvent]);

  // --- explicit action tracker ---
  const trackAction = useCallback(
    (eventType: EventType, metadata?: Record<string, unknown>) => {
      try {
        logEvent(eventType, metadata);
      } catch {
        // never throw
      }
    },
    [logEvent],
  );

  return { trackAction };
}
